import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import JsonLd from '@/components/base/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.name}`,
  description: `${siteInfo.name}品牌介绍、平台信誉与联系方式`,
  url: `${siteInfo.url}/about`,
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
  },
};

const milestones = [
  { year: '2018', title: '平台创立', desc: '91视频网站在北京正式成立，以免费高清视频观看为核心定位，开启在线视频服务之路。' },
  { year: '2019', title: '用户突破', desc: '注册用户突破1000万，平台完成首轮技术架构升级，支持4K超清播放。' },
  { year: '2020', title: '内容扩展', desc: '视频内容总量突破10万部，新增纪录片、体育、音乐等分类频道，覆盖更广泛用户群体。' },
  { year: '2021', title: '技术革新', desc: '引入AI智能推荐系统，全球部署CDN加速节点，为全球用户提供流畅的观影体验。' },
  { year: '2023', title: '规模飞跃', desc: '日活用户突破1500万，视频总量突破30万部，成为国内领先的免费视频平台之一。' },
  { year: '2026', title: '行业领先', desc: '注册用户突破8000万，日活超2000万，视频总量超50万部，稳居行业第一梯队。' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '内容安全',
    desc: '严格的内容审核机制，确保所有视频内容合规合法，为用户提供安全的观影环境。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户至上',
    desc: '以用户体验为核心，持续优化播放质量和界面交互，让每一次观影都成为享受。',
  },
  {
    icon: 'ri-rocket-2-line',
    title: '技术创新',
    desc: '不断投入技术研发，在视频编解码、智能推荐、低延迟传输等领域保持领先。',
  },
  {
    icon: 'ri-hand-heart-line',
    title: '社会责任',
    desc: '积极传播正能量内容，支持原创视频创作者，推动网络视听行业健康发展。',
  },
];

export default function About() {
  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description={`了解${siteInfo.name}的品牌故事、发展历程、平台信誉与联系方式。${siteInfo.description}`}
        keywords="91视频,91视频官网,91视频关于我们,91视频联系方式,91视频品牌,91视频信誉"
        canonicalUrl={`${siteInfo.url}/about`}
        ogTitle={`关于我们 - ${siteInfo.name}`}
        ogDescription={`了解${siteInfo.name}的品牌故事、发展历程与联系方式。`}
      />
      <JsonLd data={aboutSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6 text-center max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-5">
                关于91视频
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                {siteInfo.name}成立于{siteInfo.founded}年，是国内领先的免费在线视频平台。
                我们致力于为全球中文用户提供海量、高清、流畅的视频观看体验。
                从电影电视剧到综艺动漫纪录片，30多个内容分类、50万+视频资源，
                每日不间断更新，让每一位用户都能在这里找到喜爱的内容。
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-12 text-center">
                发展历程
              </h2>
              <div className="max-w-3xl mx-auto">
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                  {milestones.map((milestone, idx) => (
                    <div
                      key={idx}
                      className={`relative flex items-start gap-6 mb-10 ${
                        idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:block flex-1"></div>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-1">
                        <div className="w-3 h-3 rounded-full bg-background-50"></div>
                      </div>
                      <div className="flex-1 bg-background-100 rounded-xl p-5">
                        <span className="text-sm font-bold text-primary-500 whitespace-nowrap">{milestone.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-1 mb-2 font-heading">{milestone.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{milestone.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-12 text-center">
                核心价值
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                {values.map((val, idx) => (
                  <div key={idx} className="text-center p-6 rounded-xl bg-background-50">
                    <div className="w-14 h-14 rounded-xl bg-accent-100 flex items-center justify-center mb-4 mx-auto">
                      <i className={`${val.icon} text-accent-500 text-2xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{val.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-12 text-center">
                平台信誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <p className="text-3xl font-bold text-primary-500 font-heading mb-2">{siteInfo.stats.totalVideos}</p>
                  <p className="text-sm text-foreground-500">视频总量</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <p className="text-3xl font-bold text-accent-500 font-heading mb-2">{siteInfo.stats.dailyVisitors}</p>
                  <p className="text-sm text-foreground-500">日活用户</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <p className="text-3xl font-bold text-secondary-500 font-heading mb-2">{siteInfo.stats.registeredUsers}</p>
                  <p className="text-sm text-foreground-500">注册用户</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 text-center">
                  <p className="text-3xl font-bold text-primary-500 font-heading mb-2">{siteInfo.stats.totalCategories}</p>
                  <p className="text-sm text-foreground-500">内容分类</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-12 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="p-6 rounded-xl bg-background-50 text-center">
                  <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-mail-line text-primary-500 text-2xl"></i>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2 font-heading">电子邮箱</h4>
                  <a href={`mailto:${siteInfo.email}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
                    {siteInfo.email}
                  </a>
                  <p className="text-xs text-foreground-400 mt-2">商务合作 / 内容投稿</p>
                </div>
                <div className="p-6 rounded-xl bg-background-50 text-center">
                  <div className="w-14 h-14 rounded-xl bg-accent-100 flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-phone-line text-accent-500 text-2xl"></i>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2 font-heading">客服电话</h4>
                  <a href={`tel:${siteInfo.phone}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
                    {siteInfo.phone}
                  </a>
                  <p className="text-xs text-foreground-400 mt-2">工作日 9:00 - 18:00</p>
                </div>
                <div className="p-6 rounded-xl bg-background-50 text-center">
                  <div className="w-14 h-14 rounded-xl bg-secondary-100 flex items-center justify-center mb-4 mx-auto">
                    <i className="ri-map-pin-line text-secondary-500 text-2xl"></i>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2 font-heading">公司地址</h4>
                  <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  <p className="text-xs text-foreground-400 mt-2">欢迎来访洽谈合作</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-lg mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4 text-center">
                商务合作
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                如果您有内容合作、广告投放或其他商务需求，欢迎通过以下方式联系我们
              </p>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <p className="text-sm text-foreground-600 mb-2">
                  请将合作方案发送至邮箱
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i>
                  发送邮件
                </a>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}